import { inject, Injectable } from '@angular/core';
import { BaseDomainModel } from '../models/base/base-domain-model';
import { AccountAPI, MemberAccountsFilter } from '../api/account-api';
import { Observable } from 'rxjs';
import { ODataResponse } from '../models/protocols/odata-response';
import { TransactionEntry } from '../models/transactions/transaction-entry';
import { AccountTypeIdEnum } from '../models/transactions/enum/account-type-id.enum';
import { MemberAccount } from '../models/transactions/member-account';
import { MemberAccountFundAllocation } from '../models/transactions/member-account-fund-allocation';
import { InvestmentInstructions } from '../models/transactions/investment-instructions';
import { MemberAccountContributionBalance } from '../models/transactions/member-account-contribution-balance';
import { MemberAccountFundDetails } from '../models/transactions/member-account-fund-details';
import { VbAccountActivityBreakdown } from '../models/transactions/vb-account-activity-breakdown';
import { map } from 'rxjs/operators';
import { FundTypeId } from '../models/enum/shared/fund-type-id';
import { ODataQueryOptions } from '../models/shared/odata-query-options';
import { VariableBenefitAccountDetails } from '../models/transactions/variable-benefit-account-details';
import { MemberVbAccountEstimatedAnnualPaymentTotalRequest } from '../models/transactions/member-vb-account-estimated-annual-payment-total-request';
import { MemberVbAccountEstimatedAnnualPaymentTotal } from '../models/transactions/member-vb-account-estimated-annual-payment-total';
import { BankAccount } from '../views/banking/models/bank-account';
import { IpMemberVbAccountEstimatedAnnualPaymentTotalRequest } from '../models/transactions/ip-member-vb-account-estimated-annual-payment-total-request';
import { VbWithdrawalAmount } from '../models/shared/vb-withdrawal-amount';
import { VbWithdrawalAmountRequest } from '../models/shared/requests/vb-withdrawal-amount-request';
import { AnnuityPensionAccountDetails } from '../models/transactions/annuity-pension-account-details';

@Injectable({
  providedIn: 'root'
})
export class AccountDomainModel extends BaseDomainModel {
  private accountAPI = inject(AccountAPI);

  public mpGetAccounts(opts: ODataQueryOptions): Observable<ODataResponse<MemberAccount>> {
    return this.accountAPI.mpGetAccounts(opts);
  }

  public mpGetInvestmentInstructionsForAccountType(
    accountTypeId: AccountTypeIdEnum,
    excludeZeroPercentFunds = false
  ): Observable<InvestmentInstructions> {
    return this.accountAPI.mpGetInvestmentInstructionsForAccountType(accountTypeId, excludeZeroPercentFunds);
  }

  public mpGetFundAllocationsForAccountType(
    accountTypeId: AccountTypeIdEnum,
    excludeCash: boolean
  ): Observable<MemberAccountFundAllocation[]> {
    return this.accountAPI.mpGetFundAllocationsForAccountType(accountTypeId, excludeCash);
  }

  public getMPContributionAccountBalances(): Observable<MemberAccountContributionBalance[]> {
    return this.accountAPI.getMPContributionAccountBalances();
  }

  public mpGetFundDetailsForAccountType(accountTypeId: AccountTypeIdEnum): Observable<MemberAccountFundDetails[]> {
    return this.accountAPI.mpGetFundDetailsForAccountType(accountTypeId);
  }

  public mpGetTransactionEntriesForAccountType(
    accountTypeId: AccountTypeIdEnum,
    opts = new ODataQueryOptions()
  ): Observable<ODataResponse<TransactionEntry>> {
    return this.accountAPI.mpGetTransactionEntriesForAccountType(accountTypeId, opts);
  }

  public ipGetMemberAccountById(memberId: number, accountId: number): Observable<MemberAccount> {
    return this.accountAPI.ipGetMemberAccountById(memberId, accountId);
  }

  public ipGetMemberAccountFundDetails(memberId: number, accountId: number): Observable<MemberAccountFundDetails[]> {
    return this.accountAPI.ipGetMemberAccountFundDetails(memberId, accountId);
  }

  public ipGetMemberAccountBalancesByFund(
    memberId: number,
    accountTypeId: AccountTypeIdEnum
  ): Observable<MemberAccountFundAllocation[]> {
    return this.accountAPI.getIPMemberAccountTypeBalancesByFund(memberId, accountTypeId);
  }

  public ipGetMemberAccounts(
    memberId: number,
    filter?: MemberAccountsFilter
  ): Observable<ODataResponse<MemberAccount>> {
    return this.accountAPI.ipGetMemberAccounts(memberId, filter);
  }

  public ipGetMemberFundAllocations(
    memberId: number,
    accountId: number[],
    excludeCash: boolean
  ): Observable<MemberAccountFundAllocation[]> {
    return this.accountAPI.ipGetMemberFundAllocations(memberId, accountId, excludeCash);
  }

  public ipGetAccountDetailsForVbAccount(memberId: number): Observable<VariableBenefitAccountDetails> {
    return this.accountAPI.ipGetAccountDetailsForVbAccount(memberId);
  }

  public mpGetAccountDetailsForVbAccount(): Observable<VariableBenefitAccountDetails> {
    return this.accountAPI.mpGetAccountDetailsForVbAccount();
  }

  public getIPContributionBalancesForAccount(
    memberId: number,
    accountId: number
  ): Observable<MemberAccountContributionBalance[]> {
    return this.accountAPI.getIPContributionBalancesForAccount(memberId, accountId);
  }

  public ipGetMemberContributionAccountInvestmentInstructions(
    memberId: number,
    excludeZeroPercentFunds = false
  ): Observable<InvestmentInstructions> {
    return this.accountAPI.ipGetMemberContributionAccountInvestmentInstructions(memberId, excludeZeroPercentFunds).pipe(
      map(instructions => {
        instructions.investmentInstructions.sort((a, b) => {
          if (a.fundId === FundTypeId.Balanced && b.fundId !== FundTypeId.Balanced) return -1;
          if (a.fundId !== FundTypeId.Balanced && b.fundId === FundTypeId.Balanced) return 1;
          return 0;
        });
        return instructions;
      })
    );
  }

  public ipGetMemberInvestmentInstructionsForAccountType(
    memberId: number,
    accountTypeId: AccountTypeIdEnum,
    excludeZeroPercentFunds = false
  ): Observable<InvestmentInstructions> {
    return this.accountAPI
      .ipGetMemberInvestmentInstructionsForAccountType(memberId, accountTypeId, excludeZeroPercentFunds)
      .pipe(
        map(instructions => {
          instructions.investmentInstructions.sort((a, b) => {
            if (a.fundId === FundTypeId.Balanced && b.fundId !== FundTypeId.Balanced) return -1;
            if (a.fundId !== FundTypeId.Balanced && b.fundId === FundTypeId.Balanced) return 1;
            return 0;
          });
          return instructions;
        })
      );
  }

  public mpConfirmDefaultInvestmentInstructions(): Observable<any> {
    return this.accountAPI.mpConfirmDefaultInvestmentInstructions();
  }

  public ipGetMemberVbAccountActivityBreakdown(memberId: number): Observable<VbAccountActivityBreakdown> {
    return this.accountAPI.ipGetMemberVbAccountActivityBreakdown(memberId);
  }

  public mpGetVbAccountActivityBreakdown(): Observable<VbAccountActivityBreakdown> {
    return this.accountAPI.mpGetVbAccountActivityBreakdown();
  }

  public ipGetEstimatedAnnualPaymentTotalForVbAccount(
    req: IpMemberVbAccountEstimatedAnnualPaymentTotalRequest
  ): Observable<MemberVbAccountEstimatedAnnualPaymentTotal> {
    return this.accountAPI.ipGetEstimatedAnnualPaymentTotalForVbAccount(req);
  }

  public mpGetEstimatedAnnualPaymentTotalForVbAccount(
    req: MemberVbAccountEstimatedAnnualPaymentTotalRequest
  ): Observable<MemberVbAccountEstimatedAnnualPaymentTotal> {
    return this.accountAPI.mpGetEstimatedAnnualPaymentTotalForVbAccount(req);
  }

  public ipGetMemberBankAccountById(
    memberId: number,
    bankAccountId: number,
    opts = new ODataQueryOptions()
  ): Observable<BankAccount> {
    return this.accountAPI.ipGetMemberBankAccountById(memberId, bankAccountId);
  }

  public getVbMinMaxWithdrawalAmounts(req: VbWithdrawalAmountRequest): Observable<VbWithdrawalAmount> {
    return this.accountAPI.getVbMinMaxWithdrawalAmounts(req);
  }

  public mpGetAnnuityAccountDetails(accountId: number): Observable<AnnuityPensionAccountDetails> {
    return this.accountAPI.mpGetAnnuityAccountDetails(accountId);
  }

  public ipGetMemberAnnuityAccountDetails(
    memberId: number,
    accountId: number
  ): Observable<AnnuityPensionAccountDetails> {
    return this.accountAPI.ipGetMemberAnnuityAccountDetails(memberId, accountId);
  }
}
